import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { ArrowLeft } from "lucide-react";

import {
  type AppioWidgetEditorRef,
  type ValidationError,
} from "appio-widget-editor";
import "appio-widget-editor/style.css";

import { EditorWrapper } from "@/components/widget/editor-wrapper";
import { PageHeader } from "@/components/layout/page-header";
import { getApiError } from "@/lib/api/errors";
import { uploadImage } from "@/lib/api/images";
import { useServiceStore } from "@/stores/service-store";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FormError } from "@/components/form-error";
import { SubmitButton } from "@/components/submit-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useDataSourcesQuery, useDataSourceSnapshotQuery } from "@/hooks/use-data-sources";
import {
  useCreateWidgetMutation,
  useUpdateWidgetMutation,
} from "@/hooks/use-widgets";
import {
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
} from "@/hooks/use-templates";
import { widgetFormSchema, type WidgetFormData } from "@/lib/validations/widget";
import type { Widget, WidgetTemplate } from "@/types/widget";

const NONE_DATA_SOURCE = "__none__";

function dataUrlToFile(dataUrl: string): Promise<File> {
  return fetch(dataUrl)
    .then((res) => res.blob())
    .then((blob) => new File([blob], "image.png", { type: blob.type }));
}

async function uploadTemplateImages(
  templateJson: string,
  serviceId: string
): Promise<string> {
  const template = JSON.parse(templateJson);
  const uploadPromises: Promise<void>[] = [];

  const walkElements = (elements: Array<Record<string, unknown>>) => {
    for (const el of elements) {
      if (
        el.type === "image" &&
        typeof el.properties === "object" &&
        el.properties !== null
      ) {
        const props = el.properties as Record<string, unknown>;
        if (typeof props.src === "string" && props.src.startsWith("data:")) {
          const dataUrl = props.src;
          uploadPromises.push(
            dataUrlToFile(dataUrl).then(async (file) => {
              const result = await uploadImage(file, { serviceId });
              props.src = result.url;
            })
          );
        }
      }
      if (Array.isArray(el.elements)) {
        walkElements(el.elements as Array<Record<string, unknown>>);
      }
    }
  };

  for (const variant of (template.variants ?? []) as Array<Record<string, unknown>>) {
    if (Array.isArray(variant.elements)) {
      walkElements(variant.elements as Array<Record<string, unknown>>);
    }
  }

  await Promise.all(uploadPromises);
  return JSON.stringify(template);
}

interface WidgetFormProps {
  mode: "create" | "edit";
  widget?: Widget;
  template?: WidgetTemplate;
}

export function WidgetForm({ mode, widget, template }: WidgetFormProps) {
  const navigate = useNavigate();
  const { data: dataSources, isLoading: dataSourcesLoading } =
    useDataSourcesQuery({ staleTime: 0 });

  const createTemplateMutation = useCreateTemplateMutation();
  const updateTemplateMutation = useUpdateTemplateMutation();
  const createWidgetMutation = useCreateWidgetMutation();
  const updateWidgetMutation = useUpdateWidgetMutation();

  const { selectedServiceId } = useServiceStore();

  const handleUploadImage = useCallback(
    async (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
    },
    []
  );

  const iosEditorRef = useRef<AppioWidgetEditorRef>(null);
  const androidEditorRef = useRef<AppioWidgetEditorRef>(null);
  const watchosEditorRef = useRef<AppioWidgetEditorRef>(null);
  const [imageUploadFailed, setImageUploadFailed] = useState(false);
  // Keys the errors so they scroll into view again on every failed save.
  const [validationCount, setValidationCount] = useState(0);
  const [editorErrors, setEditorErrors] = useState<{
    ios: ValidationError[] | null;
    android: ValidationError[] | null;
    watchos: ValidationError[] | null;
  }>({ ios: null, android: null, watchos: null });
  const [activeEditor, setActiveEditor] = useState<"ios" | "android" | "watchos" | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const checkSmallScreen = useCallback(() => {
    // Small when the preview area (minus the two 300px editor panels) is under
    // a third of the screen.
    const width = window.innerWidth;
    const previewWidth = width - 600;
    setIsSmallScreen(previewWidth < width / 3);
  }, []);

  useEffect(() => {
    checkSmallScreen();
    window.addEventListener("resize", checkSmallScreen);
    return () => window.removeEventListener("resize", checkSmallScreen);
  }, [checkSmallScreen]);

  const isCreate = mode === "create";

  const defaultValues: WidgetFormData =
    mode === "edit" && widget && template
      ? {
          name: widget.name,
          data_source_id: widget.data_source_id,
          published: widget.published,
          template_ios: template.template_ios || "",
          template_android: template.template_android || "",
          template_watchos: template.template_watchos || "",
        }
      : {
          name: "",
          data_source_id: null,
          published: false,
          template_ios: "",
          template_android: "",
          template_watchos: "",
        };

  const form = useForm<WidgetFormData>({
    resolver: zodResolver(widgetFormSchema),
    defaultValues,
  });

  const watchedDataSourceId = form.watch("data_source_id");
  const hasDataSource = !!watchedDataSourceId && watchedDataSourceId !== NONE_DATA_SOURCE;
  const selectedDataSource = hasDataSource ? dataSources?.find((d) => d.id === watchedDataSourceId) : undefined;
  const customerUserId = (selectedDataSource?.definition?.user_id as string) || "";
  const { data: snapshotData, isLoading: snapshotLoading } = useDataSourceSnapshotQuery(
    hasDataSource ? watchedDataSourceId : "",
    customerUserId
  );
  const editorDataSource = useMemo(() => {
    if (!snapshotData?.snapshot) return {};
    try {
      return JSON.parse(snapshotData.snapshot) as Record<string, unknown>;
    } catch {
      return {};
    }
  }, [snapshotData]);
  // The reset below restores the data source through a ref, not a dependency:
  // a new snapshot (picking another data source) must not rerun it, which
  // would reset the form to the saved widget and drop unsaved edits.
  const editorDataSourceRef = useRef(editorDataSource);
  editorDataSourceRef.current = editorDataSource;

  useEffect(() => {
    iosEditorRef.current?.setDataSource(editorDataSource);
    androidEditorRef.current?.setDataSource(editorDataSource);
    watchosEditorRef.current?.setDataSource(editorDataSource);
  }, [editorDataSource]);

  // Extra safeguard: re-push the data source when the active editor changes.
  useEffect(() => {
    if (activeEditor === "ios") {
      iosEditorRef.current?.setDataSource(editorDataSource);
    } else if (activeEditor === "android") {
      androidEditorRef.current?.setDataSource(editorDataSource);
    } else if (activeEditor === "watchos") {
      watchosEditorRef.current?.setDataSource(editorDataSource);
    }
  }, [activeEditor, editorDataSource]);

  useEffect(() => {
    if (mode === "edit" && widget && template) {
      form.reset({
        name: widget.name,
        data_source_id: widget.data_source_id,
        published: widget.published,
        template_ios: template.template_ios || "",
        template_android: template.template_android || "",
        template_watchos: template.template_watchos || "",
      });
      if (template.template_ios) {
        iosEditorRef.current?.load(template.template_ios);
      }
      if (template.template_android) {
        androidEditorRef.current?.load(template.template_android);
      }
      if (template.template_watchos) {
        watchosEditorRef.current?.load(template.template_watchos);
      }
      // load() overwrites the editor's data source with {}, so restore it.
      iosEditorRef.current?.setDataSource(editorDataSourceRef.current);
      androidEditorRef.current?.setDataSource(editorDataSourceRef.current);
      watchosEditorRef.current?.setDataSource(editorDataSourceRef.current);
    }
  }, [widget, template, form, mode]);

  useEffect(() => {
    if (mode === "edit" && dataSources && widget) {
      const currentDataSourceId = form.getValues("data_source_id");
      if (!currentDataSourceId) return; // null means intentionally no data source
      const dataSourceExists = dataSources.some(
        (ds) => ds.id === currentDataSourceId
      );
      if (!dataSourceExists) {
        form.setValue("data_source_id", null, { shouldValidate: true });
      }
    }
  }, [dataSources, widget, form, mode]);

  // Set around the image uploads too, which run before any mutation starts.
  const [isSaving, setIsSaving] = useState(false);

  const mutationError =
    createTemplateMutation.error ||
    updateTemplateMutation.error ||
    createWidgetMutation.error ||
    updateWidgetMutation.error;
  // Template names are unique per service, and each widget's template is named
  // after it (deleting a widget leaves its template behind), so a reused name
  // answers 409 on create.
  const nameTaken = getApiError(createTemplateMutation.error).status === 409;

  const clearErrors = () => {
    setImageUploadFailed(false);
    createTemplateMutation.reset();
    updateTemplateMutation.reset();
    createWidgetMutation.reset();
    updateWidgetMutation.reset();
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();

    iosEditorRef.current?.setDataSource(editorDataSource);
    androidEditorRef.current?.setDataSource(editorDataSource);
    watchosEditorRef.current?.setDataSource(editorDataSource);

    // Pass the data source directly: setDataSource dispatches async, so the
    // editors' own state may still be stale here.
    const iosErrors = iosEditorRef.current?.validate(undefined, editorDataSource) ?? null;
    const androidErrors = androidEditorRef.current?.validate(undefined, editorDataSource) ?? null;
    const watchosErrors = watchosEditorRef.current?.validate(undefined, editorDataSource) ?? null;
    setEditorErrors({ ios: iosErrors, android: androidErrors, watchos: watchosErrors });
    setValidationCount((count) => count + 1);

    if (iosErrors || androidErrors || watchosErrors) {
      return;
    }

    const iosTemplate = iosEditorRef.current?.export();
    const androidTemplate = androidEditorRef.current?.export();
    const watchosTemplate = watchosEditorRef.current?.export();

    let iosJson = iosTemplate ? JSON.stringify(iosTemplate) : "";
    let androidJson = androidTemplate ? JSON.stringify(androidTemplate) : "";
    let watchosJson = watchosTemplate ? JSON.stringify(watchosTemplate) : "";

    setIsSaving(true);
    try {
      if (selectedServiceId) {
        try {
          if (iosJson) iosJson = await uploadTemplateImages(iosJson, selectedServiceId);
          if (androidJson) androidJson = await uploadTemplateImages(androidJson, selectedServiceId);
          if (watchosJson) watchosJson = await uploadTemplateImages(watchosJson, selectedServiceId);
        } catch {
          setImageUploadFailed(true);
          return;
        }
      }

      form.setValue("template_ios", iosJson);
      form.setValue("template_android", androidJson);
      form.setValue("template_watchos", watchosJson);

      await form.handleSubmit(onSubmit)();
    } finally {
      setIsSaving(false);
    }
  };

  const onSubmit = async (data: WidgetFormData) => {
    const resolvedDataSourceId = data.data_source_id || null;

    try {
      if (isCreate) {
        const templateResult = await createTemplateMutation.mutateAsync({
          name: `${data.name} Template`,
          template_ios: data.template_ios || "",
          template_android: data.template_android || "",
          template_watchos: data.template_watchos || "",
        });

        const widgetResult = await createWidgetMutation.mutateAsync({
          name: data.name,
          data_source_id: resolvedDataSourceId,
          template_id: templateResult.id,
          published: data.published,
        });

        navigate(`/widgets/${widgetResult.id}`);
      } else if (widget && template) {
        await updateTemplateMutation.mutateAsync({
          id: template.id,
          data: {
            name: `${data.name} Template`,
            template_ios: data.template_ios || undefined,
            template_android: data.template_android || undefined,
            template_watchos: data.template_watchos || undefined,
          },
        });

        await updateWidgetMutation.mutateAsync({
          id: widget.id,
          data: {
            name: data.name,
            data_source_id: resolvedDataSourceId,
            published: data.published,
          },
        });

        navigate(`/widgets/${widget.id}`);
      }
    } catch {
      // Error is handled by mutation state
    }
  };

  const pageTitle = isCreate ? "Create Widget" : "Edit Widget";
  const submitButtonText = isCreate ? "Create Widget" : "Save Changes";
  const submittingButtonText = isCreate ? "Creating..." : "Saving...";

  return (
    <>
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: "Widgets", to: "/widgets" },
          ...(widget ? [{ label: widget.name, to: `/widgets/${widget.id}` }] : []),
        ]}
        title={pageTitle}
        actions={
          <SubmitButton
            form="widget-form"
            pending={isSaving}
            pendingText={submittingButtonText}
            disabled={hasDataSource && snapshotLoading}
          >
            {submitButtonText}
          </SubmitButton>
        }
      />

      <Card className="mx-auto w-full max-w-3xl">
        <CardHeader>
          <CardTitle>Widget Details</CardTitle>
          <CardDescription>
            Configure your widget settings and templates.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            id="widget-form"
            onSubmit={handleFormSubmit}
            onChange={clearErrors}
            className="space-y-6"
          >
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="My Widget"
                maxLength={255}
                {...form.register("name")}
                aria-invalid={!!form.formState.errors.name}
                aria-describedby={
                  form.formState.errors.name ? "name-error" : undefined
                }
              />
              {form.formState.errors.name && (
                <p
                  id="name-error"
                  className="text-[13px] text-destructive"
                  role="alert"
                >
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="data_source_id">
                Data Source
              </Label>
              <Controller
                name="data_source_id"
                control={form.control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE_DATA_SOURCE}
                    onValueChange={(value) =>
                      field.onChange(value === NONE_DATA_SOURCE ? null : value)
                    }
                    disabled={dataSourcesLoading}
                  >
                    <SelectTrigger
                      id="data_source_id"
                    >
                      <SelectValue placeholder="Select a data source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_DATA_SOURCE}>None</SelectItem>
                      {dataSources?.map((ds) => (
                        <SelectItem key={ds.id} value={ds.id}>
                          {ds.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="published" className="text-sm">
                  Published
                </Label>
                <p className="text-[13px] text-muted-foreground">
                  Make this widget visible to users
                </p>
              </div>
              <Controller
                name="published"
                control={form.control}
                render={({ field }) => (
                  <Switch
                    id="published"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Templates <span className="text-destructive">*</span>
              </Label>
              <div className="flex flex-wrap gap-3">
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => setActiveEditor("ios")}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11" />
                  </svg>
                  Edit iOS Template
                </Button>
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => setActiveEditor("android")}
                >
                  <svg viewBox="0 0 152 89" fill="none">
                    <g clipPath="url(#android-icon)">
                      <path fill="currentColor" d="M151.025 85.224q-.071-.464-.147-.92a75.665 75.665 0 0 0-7.546-22.597 76.5 76.5 0 0 0-5.511-8.995 76 76 0 0 0-8.322-9.808 76.034 76.034 0 0 0-13.398-10.626q.042-.074.085-.148 2.286-3.948 4.572-7.897l4.47-7.712a3946 3946 0 0 0 3.208-5.54q.38-.658.604-1.355a6.97 6.97 0 0 0-.652-5.702 6.9 6.9 0 0 0-2.406-2.398 7 7 0 0 0-2.954-.95 7 7 0 0 0-2.376.206 6.93 6.93 0 0 0-4.22 3.227q-1.606 2.77-3.208 5.54l-4.47 7.712c-1.523 2.634-3.05 5.263-4.573 7.897q-.25.43-.5.865c-.232-.092-.46-.184-.692-.272-8.398-3.205-17.511-4.958-27.036-4.958q-.39-.001-.78.004A75.7 75.7 0 0 0 50.977 25q-1.317.46-2.608.968-.234-.404-.467-.806-2.286-3.95-4.573-7.897l-4.47-7.713a4385 4385 0 0 1-3.208-5.54A6.93 6.93 0 0 0 29.055.58a6.9 6.9 0 0 0-2.954.95 6.92 6.92 0 0 0-3.157 4.185 6.96 6.96 0 0 0 .703 5.27l3.208 5.54 4.47 7.713c1.523 2.634 3.05 5.263 4.573 7.897.01.022.025.044.036.066a76.3 76.3 0 0 0-13.527 10.711 76.5 76.5 0 0 0-8.322 9.808 75.4 75.4 0 0 0-5.51 8.995 75.7 75.7 0 0 0-7.546 22.597 76.038 76.038 0 0 0-.581 4.247h151a77 77 0 0 0-.434-3.327z" />
                      <path fill="white" d="M115.225 67.663c3.022-2.012 3.461-6.668.981-10.4-2.48-3.73-6.939-5.123-9.96-3.11-3.021 2.012-3.46 6.668-.98 10.4 2.479 3.73 6.938 5.123 9.959 3.11M46.762 64.564c2.48-3.73 2.04-8.387-.98-10.4-3.022-2.012-7.481-.619-9.96 3.112s-2.041 8.387.98 10.4 7.48.62 9.96-3.112" />
                    </g>
                    <defs><clipPath id="android-icon"><rect width="152" height="88" x=".459" y=".555" fill="white" /></clipPath></defs>
                  </svg>
                  Edit Android Template
                </Button>
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => setActiveEditor("watchos")}
                >
                  <svg viewBox="0 0 14.7266 20.9082" fill="currentColor"><path d="m.566406 14.4043c0 1.5137.595704 2.6269 1.699214 3.2324.53711.2832.84961.6445 1.04493 1.2988l.3125 1.045c.18554.625.625.9277 1.27929.9277h4.51172c.67384 0 1.08394-.293 1.26954-.9277l.3223-1.045c.1953-.6543.5078-1.0156 1.0449-1.2988 1.1035-.6055 1.6992-1.7187 1.6992-3.2324v-7.90039c0-1.51368-.5957-2.61719-1.6992-3.23243-.5371-.2832-.8496-.64453-1.0449-1.28906l-.3223-1.054686c-.166-.615234-.6055-.927734-1.26954-.927734h-4.51172c-.65429 0-1.09375.302734-1.27929.927734l-.3125 1.054686c-.18555.63477-.49805 1.01563-1.04493 1.28906-1.09374.58594-1.699214 1.69922-1.699214 3.23243zm1.425784-.2051v-7.48045c0-1.45508.84961-2.33398 2.27539-2.33398h5.78122c1.4258 0 2.2754.8789 2.2754 2.33398v7.48045c0 1.4453-.8496 2.3242-2.2754 2.3242h-5.78122c-1.42578 0-2.27539-.8789-2.27539-2.3242zm11.54301-4.43358h.3515c.5078 0 .8399-.34179.8399-.89843v-1.31836c0-.55664-.3321-.89844-.8399-.89844h-.3515z" fillOpacity=".85"/></svg>
                  Edit WatchOS Template
                </Button>
              </div>
              {(editorErrors.ios || editorErrors.android || editorErrors.watchos) && (
                <FormError key={validationCount} className="space-y-1">
                  {editorErrors.ios?.map((err, i) => (
                    <p key={`ios-${i}`}>iOS: {err.path}: {err.message}</p>
                  ))}
                  {editorErrors.android?.map((err, i) => (
                    <p key={`android-${i}`}>Android: {err.path}: {err.message}</p>
                  ))}
                  {editorErrors.watchos?.map((err, i) => (
                    <p key={`watchos-${i}`}>WatchOS: {err.path}: {err.message}</p>
                  ))}
                </FormError>
              )}
            </div>

            {imageUploadFailed && (
              <FormError>
                Failed to upload the template images. Please try again.
              </FormError>
            )}

            {mutationError && (
              <FormError>
                <p>
                  {nameTaken
                    ? "This name is already taken. Choose another widget name."
                    : mutationError instanceof AxiosError &&
                      mutationError.response?.data?.error?.message
                    ? mutationError.response.data.error.message
                    : `Failed to ${isCreate ? "create" : "update"} widget. Please try again.`}
                </p>
              </FormError>
            )}
          </form>
        </CardContent>
      </Card>
    </div>

    {/* Always mounted so the editor refs are available for load(). */}
    <div className={activeEditor !== null ? "fixed inset-0 z-50 bg-background flex flex-col" : "fixed inset-0 -z-50 opacity-0 pointer-events-none flex flex-col"}>
      <div className="flex items-center justify-between border-b px-4 h-14">
        <div className="flex items-center gap-4">
          {!isSmallScreen && (
            <button
              type="button"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={() => setActiveEditor(null)}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          )}
          <h2 className="text-lg font-semibold">
            {activeEditor === "ios" ? "iOS" : activeEditor === "android" ? "Android" : "WatchOS"} Template
          </h2>
        </div>
        {!isSmallScreen && (
          <p className="text-sm text-muted-foreground">
            This preview is an approximation. We recommend testing on a real device.
          </p>
        )}
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          onClick={() => setActiveEditor(null)}
        >
          Save template
        </Button>
      </div>
      <div className="flex-1 relative">
        <div className={activeEditor === "ios" ? "absolute inset-0" : "absolute inset-0 opacity-0 pointer-events-none"}>
          <EditorWrapper ref={iosEditorRef} scheme="ios" dataSource={editorDataSource} active={activeEditor === "ios"} onUploadImage={handleUploadImage} />
        </div>
        <div className={activeEditor === "android" ? "absolute inset-0" : "absolute inset-0 opacity-0 pointer-events-none"}>
          <EditorWrapper ref={androidEditorRef} scheme="android" dataSource={editorDataSource} active={activeEditor === "android"} onUploadImage={handleUploadImage} />
        </div>
        <div className={activeEditor === "watchos" ? "absolute inset-0" : "absolute inset-0 opacity-0 pointer-events-none"}>
          <EditorWrapper ref={watchosEditorRef} scheme="watchos" dataSource={editorDataSource} active={activeEditor === "watchos"} onUploadImage={handleUploadImage} />
        </div>
      </div>
    </div>
    </>
  );
}
